import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import {
  StaffPermission,
  hasEffectivePermission,
} from "@/lib/staffPermissions";
import { localAuthRepository } from "@/repository/localAuthRepository";

type StaffPermissionGuardProps = {
  permission?: StaffPermission;
  /** 指定した権限をすべて持つ場合だけ表示する（AND条件）。 */
  permissions?: readonly StaffPermission[];
  /** 全施設担当（GLOBAL）の場合だけ表示する。 */
  requireGlobalFacilityScope?: boolean;
  children: ReactNode;
  redirectTo?: string;
};

/**
 * メニュー非表示だけに頼らず、URLを直接入力した場合も機能権限で遮断する。
 * API側の認可と組み合わせるクライアント側の早期ガード。
 */
export function StaffPermissionGuard({
  permission,
  permissions,
  requireGlobalFacilityScope = false,
  children,
  redirectTo = "/dashboard",
}: StaffPermissionGuardProps) {
  const userInfo = localAuthRepository.getUserInfo();
  const requiredPermissions = permissions ?? (permission ? [permission] : []);
  const hasRequiredPermissions =
    requiredPermissions.length > 0 &&
    requiredPermissions.every((requiredPermission) =>
      hasEffectivePermission(
        userInfo?.role,
        requiredPermission,
        userInfo?.effectivePermissions,
        userInfo?.permissions
      )
    );
  const hasRequiredFacilityScope =
    !requireGlobalFacilityScope || userInfo?.facilityScope === "GLOBAL";
  const isAllowed = hasRequiredPermissions && hasRequiredFacilityScope;

  if (!isAllowed) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}
